/**
 * `/dashboard` — the role-agnostic entry point.
 *
 * Every email we send links here, because the sender cannot know whether the
 * reader is a platform admin, an org admin, or crew (and a freelancer may be
 * different things in different organizations). This resolves it in the browser,
 * where the session actually is, and redirects.
 *
 * Signed out, it falls through to /login, which already sends people to the
 * right dashboard once they authenticate.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { resolveDashboardPath } from '../utils/dashboardPath';

export default function DashboardRedirect() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { currentOrg, isLoading: orgLoading } = useOrganization();
  const location = useLocation();

  // Wait for org context too: redirecting on a half-loaded session would send
  // crew to /admin and bounce them straight back out again.
  if (authLoading || (isAuthenticated && orgLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div
          className="w-12 h-12 border-4 border-hairline border-t-rack rounded-full animate-spin"
          role="status"
          aria-label="Loading your dashboard"
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Navigate to={resolveDashboardPath(user, currentOrg)} replace />;
}
